import {
  DetailHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Markdown,
  READING_MEASURE,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ArticleCompactHeader } from "~/components/news/ArticleCompactHeader";
import { ArticleCover } from "~/components/news/ArticleCover";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { track } from "~/lib/analytics";
import { ApiError, fetchDashboardHighlightDetail } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useDwellTime } from "~/lib/use-dwell-time";
import { useFireOnce } from "~/lib/use-fire-once";
import { useScrollDepth } from "~/lib/use-scroll-depth";
import { AnalyticsEventName } from "~/types/analytics";

function FullscreenHighlight({ children }: { children: ReactNode }) {
  return (
    <Box
      data-testid="highlights.fullscreen"
      sx={{ minHeight: "100dvh", backgroundColor: "background.paper" }}
    >
      <Screen maxWidth="md" sx={{ paddingBottom: { xs: 6, md: 8 } }}>
        {children}
      </Screen>
    </Box>
  );
}

/** Fullscreen, shareable reader for the currently visible dashboard highlight. */
export function DashboardHighlightRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { slug = "" } = useParams<{ slug: string }>();
  const titleRef = useRef<HTMLHeadingElement>(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.dashboardHighlightDetail(locale, slug),
    queryFn: ({ signal }) =>
      fetchDashboardHighlightDetail(slug, locale, signal),
    enabled: slug.length > 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
  });

  const bodyRef = useRef<HTMLDivElement>(null);
  const viewKey = data?.id ?? null;
  const subject = {
    subjectType: "highlight" as const,
    subjectId: data?.id ?? null,
    subjectKey: data?.slug ?? null,
  };

  useFireOnce(viewKey, () => {
    track(AnalyticsEventName.ContentView, subject);
  });

  useDwellTime({
    key: viewKey,
    onSettle: (milliseconds, viewId, reason) => {
      track(AnalyticsEventName.ContentDwell, {
        ...subject,
        value: milliseconds,
        props: { view_id: viewId, reason },
      });
    },
  });

  useScrollDepth(bodyRef, viewKey, (depth) => {
    track(AnalyticsEventName.ContentScroll, { ...subject, value: depth });
  });

  const backLink = (
    <DetailHeader
      backTo="/news"
      backLabel={t("highlights.backToDashboard")}
      backTestId="highlight.back"
    />
  );

  if (isPending) {
    return (
      <FullscreenHighlight>
        {backLink}
        <LoadingState />
      </FullscreenHighlight>
    );
  }

  if (isError && error instanceof ApiError && error.status === 404) {
    return (
      <FullscreenHighlight>
        {backLink}
        <EmptyState message={t("highlights.notFound")} />
      </FullscreenHighlight>
    );
  }

  if (isError) {
    return (
      <FullscreenHighlight>
        {backLink}
        <ErrorState
          message={t("highlights.error")}
          onRetry={() => void refetch()}
        />
      </FullscreenHighlight>
    );
  }

  return (
    <Box
      data-testid="highlights.fullscreen"
      sx={{ minHeight: "100dvh", backgroundColor: "background.paper" }}
    >
      <ArticleCompactHeader
        standalone
        title={data.title}
        backTo="/news"
        backLabel={t("highlights.backToDashboard")}
        revealAfterRef={titleRef}
      />

      <Screen maxWidth="md" sx={{ paddingBottom: { xs: 6, md: 8 } }}>
        <Box
          component="article"
          data-testid="highlights.detail"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, md: 4 },
          }}
        >
          {backLink}

          <ArticleCover slug={data.slug} src={data.cover_image_url} alt="" />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxWidth: READING_MEASURE,
            }}
          >
            <Typography
              ref={titleRef}
              variant="H1"
              sx={{ color: "text.primary" }}
              data-testid="highlight.title"
            >
              {data.title}
            </Typography>

            <Typography
              component="p"
              variant="H3"
              sx={{ color: "text.secondary" }}
            >
              {data.summary}
            </Typography>
          </Box>

          <Box ref={bodyRef} sx={{ maxWidth: READING_MEASURE }}>
            <Markdown>{data.body}</Markdown>
          </Box>
        </Box>
      </Screen>
    </Box>
  );
}
